import { CommonModule } from '@angular/common';
import {
  Component,
  Inject,
  NO_ERRORS_SCHEMA,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import {
  MatLegacyCheckboxChange as MatCheckboxChange,
  MatLegacyCheckboxModule as MatCheckboxModule,
} from '@angular/material/legacy-checkbox';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogModule as MatDialogModule,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import {
  MatLegacySnackBar as MatSnackBar,
  MatLegacySnackBarModule as MatSnackBarModule,
} from '@angular/material/legacy-snack-bar';
import { AgGridModule } from 'ag-grid-angular';
import {
  CellClickedEvent,
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  PaginationChangedEvent,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';

// CONFIG

// SERVICES

// INTERFACES

// MODULES
import { AgGridSearchLocationsComponent } from 'v2/modules/shared/components/ag-grid-search-locations/ag-grid-search-locations.component';
import { DecimalPlacesPipe } from 'v2/modules/shared/pipes/decimal-places.pipe';
import { UiModule } from 'v2/modules/ui/ui.module';

import { ESalesOrderItemComposerType } from '../../configs';
import { ISalesOrderRollbackItem } from '../../interfaces';

import { LocationService } from './../../../../../app/core/locations/services/location.service';
import { SnackBarConfig } from './../../../shared/configs/snackbar.config';
import { PipeModule } from './../../../shared/pipes/pipe.module';
import { OrderSalesApiService } from './../../../shared/services/api/order/order-sales-api.service';

export interface ISalesOrderConfirmationDialogData {
  salesOrderIds :number[];
}

@Component({
  selector: 'lum-sales-order-confirmation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    FormsModule,
    ReactiveFormsModule,
    PipeModule,
    UiModule,
    MatProgressSpinnerModule,
    AgGridModule,
    // COMPONENTS
    AgGridSearchLocationsComponent,
  ],
  templateUrl: './sales-order-confirmation-dialog.component.html',
  styleUrls: ['./sales-order-confirmation-dialog.component.scss'],
  providers: [LocationService, OrderSalesApiService],
  schemas: [NO_ERRORS_SCHEMA],
})
export class SalesOrderConfirmationDialogComponent implements OnInit, OnDestroy {
  @ViewChild('agGrid') agGrid! :AgGridModule;

  form :FormGroup;
  salesOrderItems :ISalesOrderRollbackItem[] = [];
  isRollbackInventory :boolean = false;
  loading :boolean = false;
  loadingLocations :boolean = false;
  submitting :boolean = false;

  gridApi! :GridApi;
  columnDefs :ColDef[] = [];
  defaultColDef :any;
  rowData :any[];
  paginationPageSize = 10;

  private onDestroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private data :ISalesOrderConfirmationDialogData,
    public dialogRef :MatDialogRef<SalesOrderConfirmationDialogComponent>,
    private locationService :LocationService,
    private orderSalesApiService :OrderSalesApiService,
    private snackbar :MatSnackBar,
    private fb :FormBuilder,
    private decimalPlacesPipe :DecimalPlacesPipe,
  ) {
    this.columnDefs = [
      { headerName: 'Order #', field: 'orderNumber' },
      { headerName: 'SKU', field: 'internalSku' },
      { headerName: 'Item', field: 'productName' },
      {
        headerName: 'Qty',
        field: 'quantity',
        cellRenderer: (params :ICellRendererParams) => {
          return this.decimalPlacesPipe.transform(params.value);
        },
      },
      {
        headerName: 'Location',
        field: 'location',
        cellEditorFramework: AgGridSearchLocationsComponent,
        singleClickEdit: true,
        editable: true,
        filter: 'agTextColumnFilter',
        onCellClicked: (params :CellClickedEvent) => {
          if (params?.value) {
            params.api.startEditingCell({
              rowIndex: params.node.rowIndex,
              colKey: params.column.getColId(),
            });
          }
        },
        valueGetter: (params :ValueGetterParams) => params.data?.locationName,
        valueSetter: (params :ValueSetterParams) => {
          const newValue = params.newValue;
          params.data.locationName = newValue?.location_name;
          const itemsArray = this.form.get('items') as UntypedFormArray;
          const itemFormGroup = itemsArray.at(params.node.rowIndex) as FormGroup;
          itemFormGroup.patchValue({
            locationId: newValue?.id,
            location: {
              id: newValue?.id,
              location_name: newValue?.location_name,
            },
          });

          return true;
        },
      },
    ];

    this.defaultColDef = {
      sortable: true,
      filter: true,
    };

    this.rowData = [];
  }

  ngOnInit() :void {
    this.initForm();

    const inputErrors :string[] = this.#getInputErrors();

    if (inputErrors.length > 0) {
      this.dialogRef.close({ dismiss: true, errors: inputErrors });
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onGridReady(params :GridReadyEvent) {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();
  }

  onRollBackInvetoryChange(event :MatCheckboxChange) :void {
    if (!this.salesOrderItems?.length && event.checked) {
      this.getSalesOrderItems().subscribe();
    }
  }

  onPageChange(event :PaginationChangedEvent) :void {
    console.log(event);
  }

  onLocationChange(event :any) :void {
    const item = this.form.get('items')?.value[event.rowIndex];
    if (item) {
      item.location = event.newValue;
      this.form.get('items')?.setValue(this.form.get('items')?.value);
    }
  }

  onSearch(searchStr :string, rowIndex :number) :void {
    this.loadingLocations = true;
    const item = this.form.get('items')?.value[rowIndex];
    this.locationService
      .getAsync({
        per_page: '10',
        page: '1',
        keyword: searchStr,
        limit: '100',
        parentWarehouseGroupId: item.warehouseGroupId?.toString(),
      })
      .subscribe({
        next: (response :any) => {
          if (response?.data?.length) {
            const columnDef = this.columnDefs.find(
              (col) => col.field === 'location',
            );
            if (columnDef) {
              columnDef.cellEditorParams.values = response.data.map(
                (location :any) => ({
                  id: location.id,
                  location_name: location.location_name,
                }),
              );
            }
            this.gridApi.setColumnDefs(this.columnDefs);
          }
        },
        complete: () => {
          this.loadingLocations = false;
        },
      });
  }

  onSubmit() :void {
    this.submitting = true;
    const { value } = this.form;
    const payload = {
      salesOrders: this.data?.salesOrderIds.map((id) => {
        const data = {
          id,
          items: [],
        };

        if (value.isRollbackInventory) {
          data.items = value.items
            .filter((item :any) => item.orderId === id && item.isOrderShipped)
            .map((item :any) => ({
              id: item.id,
              locationId: item.location?.id,
              orderItemId: item.orderItemId,
              rfqId: item.rfqId,
            }));
        }

        return data;
      }),
      rollbackInventory: true,
    };

    this.orderSalesApiService.bulkDeleteOrders(payload).subscribe({
      next: (response :any) => {
        this.submitting = false;
        this.dialogRef.close(response);
        this.snackbar.open(
          response?.message || 'Successfully deleted sales orders',
          'Success',
          {
            duration: SnackBarConfig.DEFAULT.duration,
          },
        );
      },
      error: (error) => {
        this.submitting = false;
        this.snackbar.open(
          error?.error?.message || 'Failed to delete sales orders',
          'Error',
          {
            duration: SnackBarConfig.DEFAULT.duration,
          },
        );
      },
    });
  }

  private initForm(items ?:ISalesOrderRollbackItem[]) :void {
    this.form = this.fb.group({
      isRollbackInventory: this.fb.control(items?.length ? true : false),
      items: this.fb.array(
        items
          ? items.map((item) =>
              this.fb.group({
                id: this.fb.control(item.id),
                orderId: this.fb.control(item.orderId),
                orderItemId: this.fb.control(item.orderItemId),
                orderNumber: this.fb.control(item.orderNumber),
                rfqId: this.fb.control(item.rfqId),
                internalSku: this.fb.control(item.internalSku),
                productName: this.fb.control(item.productName),
                quantity: this.fb.control(item.quantity),
                warehouseGroupId: this.fb.control(item.warehouseGroupId),
                locationId: this.fb.control(item.locationId),
                locationName: this.fb.control(item.locationName),
                location: this.fb.group({
                  id: this.fb.control(item.locationId),
                  location_name: this.fb.control(item.locationName),
                }),
                locations: this.fb.array([]),
                orderStatus: this.fb.control(item.orderStatus),
                isOrderShipped: this.fb.control(item.isOrderShipped),
              }),
            )
          : [],
      ),
    });

    if (items) {
      this.rowData = items.filter((item) => item.isOrderShipped);
    }
  }

  private getSalesOrderItems() :Observable<ISalesOrderRollbackItem[]> {
    this.loading = true;

    return this.orderSalesApiService
      .getComposedItems({
        salesOrderIds: this.data?.salesOrderIds.map((id) => id).join(','),
        type: ESalesOrderItemComposerType.ROLLBACK,
      })
      .pipe(
        tap((response) => {
          this.salesOrderItems = response;
          this.initForm(response);
        }),
        finalize(() => (this.loading = false)),
        takeUntil(this.onDestroy$),
      );
  }

  get hasShippedOrders() :boolean {
    return this.salesOrderItems.some((item) => item.isOrderShipped);
  }

  #getInputErrors() {
    const inputErrors = [];

    // salesOrderIds
    if (
      !Array.isArray(this.data?.salesOrderIds) ||
      this.data.salesOrderIds.length < 0
    ) {
      const devNote = `Missing required 'data.salesOrderIds'.`;

      // info for devs
      console.error(devNote);
      inputErrors.push(devNote);

      // ask the user to contact support, since this would be an issue in the
      // code (and NOT user error).
      this.snackbar.open(
        'Whoops! Something went wrong; please contact support.',
        'Dismiss',
        { ...SnackBarConfig.ERROR },
      );
    }

    return inputErrors;
  }
}
